'use client';

import { useId, useRef, useState, type FormEvent } from 'react';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useRouter } from 'next/navigation';

import Link from '@eliadmin/core/src/components/link';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
export type { ContactFormValues } from './contact-form-validation';

/**
 * Native, uncontrolled fields retain drafts and browser autofill without a form
 * runtime per page. The original Zod rules and API load only when submitting.
 */
export function ContactForm() {
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const id = useId();
  const [gdpr, setGdpr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submissionInFlight = useRef(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const clearError = (name: string) =>
    setErrors(previous => {
      if (!previous[name]) return previous;
      const next = { ...previous };
      delete next[name];
      return next;
    });
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submissionInFlight.current) return;
    // Capture before awaiting: the same form stays mounted throughout submission.
    const values = new FormData(event.currentTarget);
    submissionInFlight.current = true;
    setSubmitting(true);
    setErrors({});
    try {
      const { validateContactForm } = await import('./contact-form-validation');
      const result = validateContactForm(
        {
          name: String(values.get('name') ?? ''),
          email: String(values.get('email') ?? ''),
          phone: String(values.get('phone') ?? ''),
          custom: { text81: String(values.get('custom.text81') ?? '') },
          text: String(values.get('text') ?? ''),
          gdpr,
        },
        formValidationMessages,
      );
      if (!result.success) {
        setErrors(
          Object.fromEntries(
            result.error.issues.map(issue => [issue.path.join('.'), issue.message]),
          ),
        );
        const invalidId = id + '-' + result.error.issues[0]?.path.join('.');
        document.getElementById(invalidId)?.focus();
        return;
      }

      const [{ sendContactMessage }, { ValidationError }] = await Promise.all([
        import('@eliadmin/core/src/api/forms/forms-api'),
        import('@eliadmin/core/src/api/errors'),
      ]);
      try {
        const captcha = '';
        await sendContactMessage({
          data: { ...result.data,  },
          locale,
        });
        toast({ title: t('text658'), variant: 'success' });
        router.push(ROUTES.HOME());
      } catch (error) {
        if (error instanceof ValidationError) {
          setErrors(
            Object.fromEntries(
              Object.entries(error.fields).map(([name, messages]) => [name, messages.join('\n')]),
            ),
          );
          return;
        }
        throw error;
      }
    } catch (error) {
      toast({
        title: t('text657'),
        description: (error as Error)?.message,
        variant: 'destructive',
      });
    } finally {
      submissionInFlight.current = false;
      setSubmitting(false);
    }
  };

  const fields = [
    {
      name: 'name',
      label: t('text33'),
      type: 'text',
      autoComplete: 'name',
      className: 'col-span-12',
      css: 'name',
    },
    {
      name: 'email',
      label: t('Email'),
      type: 'email',
      autoComplete: 'email',
      className: 'col-span-12 sm:col-span-6',
      css: 'email',
    },
    {
      name: 'phone',
      label: t('Telefón'),
      type: 'tel',
      autoComplete: 'tel',
      className: 'col-span-12 sm:col-span-6',
      css: 'name',
    },
    {
      name: 'custom.text81',
      label: t('Predmet'),
      type: 'text',
      autoComplete: 'off',
      className: 'col-span-12',
      css: 'name',
    },
  ];
  const errorMessage = (name: string) =>
    errors[name] ? (
      <p
        id={id + '-' + name + '-error'}
        role="alert"
        className="text-sm font-medium text-destructive"
      >
        {errors[name]}
      </p>
    ) : null;

  return (
    <form
      noValidate
      onSubmit={handleSubmit}
      className="ea-contact-form grid w-full grid-cols-12 gap-4 @container"
    >
      {fields.map(field => (
        <div
          key={field.name}
          className={
            field.className +
            ' ea-contact-form__field ea-contact-form__ space-y-2' +
            field.css +
            '-field'
          }
        >
          <div
            className={
              'ea-contact-form__input-wrapper ea-contact-form__ flex w-full flex-row items-center space-x-2' +
              field.css +
              '-input-wrapper'
            }
          >
            <Input
              id={id + '-' + field.name}
              name={field.name}
              type={field.type}
              autoComplete={field.autoComplete}
              placeholder={field.label + ' *'}
              aria-label={field.label}
              aria-required="true"
              aria-invalid={!!errors[field.name]}
              aria-describedby={errors[field.name] ? id + '-' + field.name + '-error' : undefined}
              rounded={CONTACT_CONFIG.form.input.rounded}
              className={{
                main: 'ea-contact-form__input ea-contact-form__' + field.css + '-input',
              }}
              onInput={() => clearError(field.name)}
            />
          </div>
          {errorMessage(field.name)}
        </div>
      ))}
      <div className="ea-contact-form__field ea-contact-form__text-field col-span-12 space-y-2">
        <div className="ea-contact-form__input-wrapper ea-contact-form__text-input-wrapper flex w-full flex-row items-center space-x-2">
          <Textarea
            id={id + '-text'}
            name="text"
            placeholder={t('text615') + ' *'}
            aria-label={t('text615')}
            rows={5}
            rounded={CONTACT_CONFIG.form.input.rounded}
            aria-invalid={!!errors.text}
            aria-describedby={errors.text ? id + '-text-error' : undefined}
            onInput={() => clearError('text')}
          />
        </div>
        {errorMessage('text')}
      </div>
      <div className="ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item col-span-12 flex items-center gap-2">
        <Checkbox
          id={id + '-gdpr'}
          name="gdpr"
          checked={gdpr}
          onCheckedChange={value => {
            setGdpr(value === true);
            clearError('gdpr');
          }}
          aria-required="true"
          aria-invalid={!!errors.gdpr}
          aria-describedby={errors.gdpr ? id + '-gdpr-error' : undefined}
          className="ea-contact-form__gdpr-checkbox ea-contact-form__checkbox"
        />
        <label
          htmlFor={id + '-gdpr'}
          className="ea-contact-form__gdpr-checkbox-label text-sm font-normal text-foreground sm:text-sm"
        >
          {t('text613')}{' '}
          <Link
            href={getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr')}
            className="ea-contact-form__gdpr-link"
            prefetch={false}
          >
            <span className="text-foreground underline">{t('text614')}</span>
          </Link>
        </label>
      </div>
      {errors.gdpr && <div className="col-span-12">{errorMessage('gdpr')}</div>}
      <div className="col-span-12">{errorMessage('g-recaptcha-response')}</div>
      <div className="col-span-12 flex w-full items-center justify-center">
        <Button
          type="submit"
          variant={CONTACT_CONFIG.form.submit.variant}
          className="ea-contact-form__submit h-[52px] w-full sm:w-fit"
          isLoading={submitting}
          aria-label={t('text185')}
          disabled={submitting}
        >
          {t('text185')}
        </Button>
      </div>
    </form>
  );
}
